import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { ZodString } from "zod";
import { z } from "zod";

import { URL_BIBLE } from "../../../lib/constants/urls/urlBible";
import type {
  BibleBookId,
  BibleManifest,
} from "../../../lib/types/schema/bible/bibleManifestSchema";
import type { CloudfrontCookie } from "../../../lib/types/schema/cloudfrontCookieSchema";
import { signCFUrl } from "../../../lib/utils/signCFUrl";
import type { UseRequestInfiniteQueryProps } from "../../../request/hooks/useRequestInfiniteQuery";
import { useRequestInfiniteQuery } from "../../../request/hooks/useRequestInfiniteQuery";
import { keyBibleHTML } from "./keyBibleHTML";

type Props = Omit<UseRequestInfiniteQueryProps, "initialPageParam"> & {
  translationId: number;
  bookAbbr: BibleBookId;
  manifest: BibleManifest;
  initialPageParam: string; // require it
  cookies: CloudfrontCookie[];
};

export const useRequestInfiniteBibleHTML = ({
  translationId,
  bookAbbr,
  manifest,
  cookies,
  ...props
}: Props) => {
  const bookManifest = manifest.books.find((bk) => bk.id === bookAbbr);

  return useRequestInfiniteQuery<ZodString>({
    enabledWithoutSessionToken: true,
    getNextPageParam: (_, __, lastPageParam: string) => {
      switch (translationId) {
        case 2: {
          switch (bookAbbr) {
            // fall-throughs are intentional
            case "Gen":
              if (lastPageParam === "bk") return "contrib";
              if (lastPageParam === "contrib") return "ot-pref";
              if (lastPageParam === "ot-pref") return "pent-intro";
              if (lastPageParam === "pent-intro") return "intro";
            case "Mat":
              if (lastPageParam === "nt-pref") return "gospel-intro";
            default:
              if (
                [
                  "pent-intro",
                  "gospel-intro",
                  "hist-intro",
                  "novella-intro",
                  "wis-intro",
                  "proph-intro",
                  "letters-intro",
                  "cath-letters-intro",
                ].includes(lastPageParam)
              )
                return "intro";
              if (lastPageParam === "intro") return "1";
              const chInt = parseInt(lastPageParam);
              if ((bookManifest?.chapter_count ?? 0) > chInt) {
                return (chInt + 1).toString();
              }
              return null;
          }
        }
        default: {
          if (lastPageParam === "intro") return "1";
          const chInt = parseInt(lastPageParam);
          if ((bookManifest?.chapter_count ?? 0) > chInt) {
            return (chInt + 1).toString();
          }
          return null;
        }
      }
    },
    getPreviousPageParam: (_, __, firstPageParam: string) => {
      switch (translationId) {
        case 2: {
          switch (bookAbbr) {
            // fall-throughs are intentional
            case "2Ki":
            case "2Sa":
              if (firstPageParam === "1") return null;
            case "Gen":
              if (firstPageParam === "intro") return "pent-intro";
              if (firstPageParam === "pent-intro") return "ot-pref";
              if (firstPageParam === "ot-pref") return "contrib";
              if (firstPageParam === "contrib") return "bk";
              if (firstPageParam === "bk") return null;
            case "Mat":
              if (firstPageParam === "intro") return "gospel-intro";
              if (firstPageParam === "gospel-intro") return "nt-pref";
              if (firstPageParam === "nt-pref") return null;
            case "Jos":
              if (firstPageParam === "intro") return "hist-intro";
              if (firstPageParam === "hist-intro") return null;
            case "Tob":
              if (firstPageParam === "intro") return "novella-intro";
              if (firstPageParam === "novella-intro") return null;
            case "Job":
              if (firstPageParam === "intro") return "wis-intro";
              if (firstPageParam === "wis-intro") return null;
            case "Isa":
              if (firstPageParam === "intro") return "proph-intro";
              if (firstPageParam === "proph-intro") return null;
            case "Rom":
              if (firstPageParam === "intro") return "letters-intro";
              if (firstPageParam === "letters-intro") return null;
            case "Jam":
              if (firstPageParam === "intro") return "cath-letters-intro";
              if (firstPageParam === "cath-letters-intro") return null;
            default:
              if (firstPageParam === "intro") return null;
              if (firstPageParam === "1") return "intro";
              return (parseInt(firstPageParam) - 1).toString();
          }
        }
        default: {
          if (bookManifest?.has_intro) {
            if (firstPageParam === "1") return "intro";
            if (firstPageParam === "intro") return null;
          }
          if (firstPageParam === "1") return null;
          return (parseInt(firstPageParam) - 1).toString();
        }
      }
    },
    queryFnRequestProps: ({ pageParam }) => ({
      schema: z.string(),
      url: signCFUrl(
        `${URL_BIBLE}/translations/book/${translationId}/${bookAbbr}/${pageParam}.html`,
        cookies,
      ),
      requestType: "other",
      raw: true,
    }),
    queryKey: keyBibleHTML({
      cookies: JSON.stringify(cookies),
      translationId,
      bookAbbr,
    }),
    ...props,
  }) as unknown as UseInfiniteQueryResult<{
    pages: string[];
    pageParams: string[];
  }>;
};
